import { styled } from "@linaria/react";
import React, { useState } from "react";
import { color, fonts } from "@ui";
import Collapse from "@ui/Collapse";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import { comparatorBy } from "diagnostics-lib/comparator";
import { StyledScrollbarDiv } from "@ui/StyledScrollbarDiv";
import t from "../../../../../../../../../../i18n";
import {
  DEFAULT_QUANTITY_FILTER_VALUES,
  filterQuantities,
  getIndicatorsLabel,
  getQuantitiesFilterMetadata,
  getRoleLabel,
  type EnhancedQuantity,
} from "../../../../../../../../Settings/Quantities/utils/quantityFilters";
import FilterSelects, { NONE_LABEL } from "@ui/Filter";
import cx from "classnames";
import { useDateRange } from "../../../../../../../../Analysis/useSelectedAnalyses";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { AddCircleLine, CloseLine, Filter3Fill, ShareBoxLine } from "@ui/IconV2/react";
import ButtonIconV2, { ButtonIconMarker } from "@ui/ButtonIconV2";
import BlankStateV2 from "@ui/BlankStateV2";
import { useMetrologyOverrides } from "../../../../../../../../../../api/useMetrologyOverrides";
import {
  useMetrologyCrossFilters,
  type MetrologyCrossFilters,
} from "../../../../../../../../../../api/useMetrologyCrossFilters";
import useActivationFilters, {
  type ActivationFilter,
} from "../../../../../../../../../../api/useActivationFilters";
import { useSearchQuantitiesInput } from "../../../../../../../../QuantitySelector/hook/useSearchQuantitiesInput";
import { useShowOnlyAlertQuantities } from "../../../../../../../../QuantitySelector/hook/useShowOnlyAlertQuantities";
import { useQuantitySelectorFilteredValues } from "../../../../../../../../QuantitySelector/hook/useQuantitySelectorFilteredValues";
import { useShowIntermediateCalculations } from "../../../../../../../../QuantitySelector/hook/useShowIntermediateCalculations";
import { useSetups } from "../../../../../../../../../../api/useSetups";
import { colors, spacings } from "@ui/Theme";
import { useRevelantQuantities } from "../../../../../../../hooks/useRevelantQuantities";
import { RelevantQuantitiesSwitch } from "../../../../../../../components/Switches/RelevantQuantitiesSwitch";
import type { Project } from "../../../../../../../../../../api/useProjects";
import { type Period } from "@ui/Highcharts/hooks/usePeriod";
import type { SelectedQuantities } from "../../../../../../../../useSelectedQuantities";
import type { ActiveAnalysis } from "../../../../../../../../../../api/useActiveAnalysesStream";
import { useDisplayAllQuantities } from "../../../../../../../hooks/createGlobalStates/useDisplayAllQuantities";
import { css } from "@linaria/core";
import NoData from "@ui/Illustration/react/NoData";
import {
  quantityRoles,
  useQuantities,
  useQuantityIndex,
  type Quantity,
} from "../../../../../../../../../../api/useQuantities";
import { literalUnion } from "diagnostics-lib/io";
import { groupBy, indexBy } from "diagnostics-lib/iterable";
import { QuantitySelectorType } from "../../../../../../../../QuantitySelector";
import Ellipsis from "@ui/Ellipsis";
import AlertBadge from "../../../../../../../../QuantitySelector/AlertBadge";
import AlertMessage from "../../../../../../../../QuantitySelector/AlertMessage";
import { QuantityGroupCard } from "../../../../../../../../QuantitySelector/QuantityIdentityCard";
import Tooltip from "../../../../../../../../QuantitySelector/Tooltip";
import formatNumber from "@ui/i18n/formatNumber";
import { useQuantityAlerts } from "../../../../../../../../QuantitySelector/useQuantityAlerts";
import Button from "@ui/Button";
import { toPath } from "../../../../../../../../../../router/helpers/path";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import Switch from "@ui/Switch";
import type { CreateFormulaQuantityHandle } from "../../../../../../../../Settings/Quantities/CreateFormulaQuantity";
import Drawer from "@ui/Drawer";
import CreateFormulaQuantity, {
  submit,
} from "../../../../../../../../Settings/Quantities/CreateFormulaQuantity";
import Text from "@ui/Text";
import { useCanUserDoOnProject } from "../../../../../../../../../canUserDo";
import { toBasenamePath } from "../../../../../../../../../../router/helpers/navigation";
import type { EditQuantityHandle } from "../../../../../../../../Settings/Quantities/EditQuantity/EditQuantity";
import EditQuantity from "../../../../../../../../Settings/Quantities/EditQuantity/EditQuantity";
import useToasts from "@ui/Toast/useToasts";
import useModal from "@ui/lib/hooks/useModal";
import Modal from "@ui/Modal";
import ConfirmDelete from "../../../../../../../../Settings/Quantities/modalForm/ConfirmDelete";
import { useDelete } from "../../../../../../../../../../api/useCall";
import type { Event } from "../../../../../../../../../../api/useEvents";
import { getEventScope } from "../../../../../../../../../../domain/event";
import SelectableQuantitiesGroup from "../../../../../../../../QuantitySelector/SelectableQuantitiesGroup";
import { useQuantitySelectorShowDescriptions } from "../../../../../../../../QuantitySelector/hook/useQuantitySelectorShowDescriptions";
import { ChartMenu } from "../../../../../../../../Chart/Menu";
import TooltipV2 from "@ui/TooltipV2";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";

const OTHERS = t("project.fault_event.quantity_selector.category.others");
const PROMOTION_FILTERS = t("project.fault_event.quantity_selector.category.promotion_filter");
const GROUPING_TYPES = ["SYSTEM", "CATEGORY"] as const;
const GroupingType = literalUnion(GROUPING_TYPES);
type GroupingType = (typeof GROUPING_TYPES)[number];

function getCategories(q: EnhancedQuantity) {
  const categories: string[] = [];

  if (q.qoiOrder !== null) categories.push(getIndicatorsLabel("performance"));
  else if (q.roles.has("observable")) categories.push(getRoleLabel("observable"));
  else if (!q.isPromoted) categories.push(OTHERS);

  if (q.isPromoted) categories.push(PROMOTION_FILTERS);

  return categories;
}

const getQuantityGroupsByGroupingType: {
  [k in GroupingType]: (q: EnhancedQuantity) => string[];
} = {
  SYSTEM: (q: EnhancedQuantity) => [q.systemId ?? NONE_LABEL],
  CATEGORY: (q: EnhancedQuantity) => getCategories(q),
};
const getQuantityGroupTitleByGroupingType: {
  [k in GroupingType]: (q: string) => string;
} = {
  SYSTEM: (_: string) => {
    throw new Error("it should not happen");
  },
  CATEGORY: (s: string | null) => s ?? NONE_LABEL,
};

interface P {
  event: Event;
  project: Project;
  selectedQuantities: SelectedQuantities;
  setSelectedQuantities: (newQuantities: Quantity[]) => void;
  onlyFirstDisplayed: boolean;
  activeAnalyses: ActiveAnalysis[];
  selectedAnalyses: ActiveAnalysis[];
  alertType: "measures" | "symptoms";
  period: Period;
  allAnalysesMode: boolean;
  page: QuantitySelectorType;
}

const useShowQuantityFilters = createGlobalState({
  storage: "session",
  key: "showQuantityFiltersFaultEvent",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const QuantitySelector = ({
  event,
  project,
  selectedQuantities,
  setSelectedQuantities,
  onlyFirstDisplayed,
  activeAnalyses,
  selectedAnalyses,
  alertType,
  period,
  allAnalysesMode,
  page,
}: P) => {
  const canUserEditQuantities = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const canUserViewIntermediateCalculations = useCanUserDoOnProject("PROJECT_SETUP", project.id);

  const [displayAllQuantities, setDisplayAllQuantities] = useDisplayAllQuantities({
    scope: getEventScope(event),
  });
  const [search, setSearch] = useSearchQuantitiesInput({
    scope: getEventScope(event),
  });
  const [showOnlyAlertQuantities, setShowOnlyAlertQuantities] = useShowOnlyAlertQuantities({
    scope: `${getEventScope(event)}:${page}`,
  });
  const [showIntermediateCalculations, setShowIntermediateCalculations] =
    useShowIntermediateCalculations({
      scope: `${getEventScope(event)}:${page}`,
    });
  const [showQuantityDescriptions, setShowQuantityDescriptions] =
    useQuantitySelectorShowDescriptions({
      scope: project.id,
    });
  const [filteredValues, setFilteredValues] = useQuantitySelectorFilteredValues({
    scope: getEventScope(event),
  });
  const [dateRange] = useDateRange({
    scope: project.id,
  });

  const [showQuantityFilters, setShowQuantityFilters] = useShowQuantityFilters({
    scope: getEventScope(event),
  });

  const toggleShowQuantityFilters = React.useCallback(() => {
    setShowQuantityFilters(value => !value);
  }, [setShowQuantityFilters]);

  const createQuantityRef = React.useRef<CreateFormulaQuantityHandle>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);
  const [quantityName, setQuantityName] = React.useState<string | null>(null);

  const handleCloseDrawer = React.useCallback(() => {
    createQuantityRef.current?.resetForm();
    setQuantityName(null);
    setIsCreateDrawerOpen(false);
  }, [createQuantityRef]);

  const [quantityToModify, setQuantityToModify] = React.useState<Quantity | null>(null);

  const editQuantityRef = React.useRef<EditQuantityHandle>(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = React.useState(false);
  const isDrawerOpen = isEditDrawerOpen || isCreateDrawerOpen;

  const { closeModal, openModal, modal: isDeleteModalOpen } = useModal();
  const { addToast } = useToasts();

  const handleEditQuantity = React.useCallback((q: Quantity) => {
    setQuantityToModify(q);
    setQuantityName(q.name);
    setIsEditDrawerOpen(true);
  }, []);

  const handleDeleteQuantity = React.useCallback(
    (q: Quantity) => {
      setQuantityToModify(q);
      openModal();
    },
    [openModal],
  );

  const deleteQuantity = useDelete("/projects/:projectId(uuid)/quantities/:quantityId(uuid)");

  const handleDelete = async () => {
    if (!quantityToModify) throw new Error("Should not happen");

    try {
      await deleteQuantity({
        routeParams: { projectId: project.id, quantityId: quantityToModify.id },
      });
      addToast("success", t("project.quantity.toast.deleted.success"));
    } catch (error) {
      addToast("error", t("errors.backend_error"));
    }

    return true;
  };

  const handleCloseEditDrawer = React.useCallback(() => {
    setIsEditDrawerOpen(false);
    setQuantityToModify(null);
    setQuantityName(null);
  }, []);

  const setups = useSetups(project.id);

  const setup =
    dateRange.pivot === null ? null : getAnalysisSetup(setups, { date: dateRange.pivot });

  const promotedQuantityIds = React.useMemo(() => {
    if (!setup || event.type === "MANUAL") return [];

    const failuresByName = indexBy(setup.failures, "name");
    const faultNames = event.metadata.faults.map(fault => fault.name);

    return [
      ...new Set(
        faultNames.flatMap(faultName => {
          const currentFailure = failuresByName.get(faultName);

          return (
            currentFailure?.filters
              .filter(filter => filter.rule === "PROMOTION")
              .map(filter => filter.quantityId) ?? []
          );
        }),
      ),
    ];
  }, [setup, event]);

  const metrologyOverrides = useMetrologyOverrides(project.id);
  const quantities = useQuantities(project.id);
  const visibleQuantities = React.useMemo(
    () =>
      quantities.filter(
        quantity =>
          !quantity.hidden || (canUserViewIntermediateCalculations && showIntermediateCalculations),
      ),
    [canUserViewIntermediateCalculations, quantities, showIntermediateCalculations],
  );

  React.useEffect(() => {
    const visibleQuantityIds = new Set(visibleQuantities.map(quantity => quantity.id));
    const selectedVisibleQuantities = selectedQuantities
      .filter(({ quantity }) => visibleQuantityIds.has(quantity.id))
      .map(({ quantity }) => quantity);

    if (selectedVisibleQuantities.length === selectedQuantities.length) return;

    const nextSelectedQuantities = selectedVisibleQuantities[0]
      ? selectedVisibleQuantities
      : visibleQuantities[0]
        ? [visibleQuantities[0]]
        : [];

    if (nextSelectedQuantities.length > 0) setSelectedQuantities(nextSelectedQuantities);
  }, [selectedQuantities, setSelectedQuantities, visibleQuantities]);
  const revelantQuantities = useRevelantQuantities({
    dataType: page,
    projectId: project.id,
    event,
    setups,
    quantities: visibleQuantities,
  });

  const crossFilters: MetrologyCrossFilters = useMetrologyCrossFilters(project.id);
  const crossFiltersByQuantityName = React.useMemo(() => {
    const crossFiltersByQuantityName: { [quantityName: string]: MetrologyCrossFilters } = {};
    for (const cf of crossFilters) {
      if (!crossFiltersByQuantityName[cf.invalidatedQuantityName]) {
        crossFiltersByQuantityName[cf.invalidatedQuantityName] = [];
      }
      crossFiltersByQuantityName[cf.invalidatedQuantityName]!.push(cf);
    }
    return crossFiltersByQuantityName;
  }, [crossFilters]);
  const quantityIndex = useQuantityIndex(project.id);
  const enhancedQuantities: EnhancedQuantity[] = React.useMemo(
    () =>
      visibleQuantities.map(q => ({
        ...q,
        roles: quantityRoles(q, setup ? [setup] : []),
        filters: [
          typeof q.minMetrology === "number" ||
          typeof q.maxMetrology === "number" ||
          typeof q.maxStdMetrology === "number" ||
          crossFilters.some(c => c.invalidatedQuantityName === q.name)
            ? "metrology"
            : null,
          typeof q.minActivation === "number" ||
          typeof q.maxActivation === "number" ||
          typeof q.maxStdActivation === "number"
            ? "activation"
            : null,
        ].filter((f): f is NonNullable<"activation" | "metrology"> => f !== null),
        isPromoted: promotedQuantityIds.includes(q.id),
      })),
    [visibleQuantities, setup, crossFilters, promotedQuantityIds],
  );

  const showRolesFilter = enhancedQuantities.some(q => !q.roles.has("others"));
  const hideUnitsFilter = enhancedQuantities.every(q => q.unit === null);

  const quantityAlerts = useQuantityAlerts({
    selectedAnalyses,
    activeAnalyses,
    period,
    alertType,
    metrologyOverrides,
    quantityIndex,
    setups,
  });

  const alertQuantityIds = React.useMemo(
    () =>
      Object.entries(quantityAlerts)
        .filter(qa => qa[1].isInAlert)
        .map(qa => qa[0]),
    [quantityAlerts],
  );
  const alertQuantities = React.useMemo(
    () => enhancedQuantities.filter(quantity => alertQuantityIds.includes(quantity.id)),
    [enhancedQuantities, alertQuantityIds],
  );

  const sortedQuantities = React.useMemo(
    () =>
      showOnlyAlertQuantities
        ? [...alertQuantities].sort(comparatorBy("name"))
        : [...enhancedQuantities].sort(comparatorBy("name")),
    [alertQuantities, showOnlyAlertQuantities, enhancedQuantities],
  );
  const filteredQuantities = React.useMemo(() => {
    const filteredQuantitiesByOptionFilter = filterQuantities(sortedQuantities, filteredValues);
    if (displayAllQuantities) return filteredQuantitiesByOptionFilter;

    const filteredByRevelantQuantities =
      event.type === "DIAGNOSED"
        ? filteredQuantitiesByOptionFilter.filter(f => revelantQuantities.some(r => f.id === r.id))
        : filteredQuantitiesByOptionFilter;
    return filteredByRevelantQuantities;
  }, [sortedQuantities, filteredValues, revelantQuantities, displayAllQuantities, event.type]);

  const [searchedQuantities] = useFilteredDatas(
    filteredQuantities,
    search,
    quantity => `${quantity.name} ${quantity.description}`,
  );
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );
  const activationFilters = useActivationFilters(project.id);
  const activationFiltersByQuantityId = React.useMemo(() => {
    const activationFiltersByQuantityId: {
      [quantityId: string]: ActivationFilter;
    } = {};
    for (const af of activationFilters) {
      activationFiltersByQuantityId[af.quantityId] = af;
    }
    return activationFiltersByQuantityId;
  }, [activationFilters]);

  const [openGroups, setOpenGroups] = React.useState<{ SYSTEM: string[]; CATEGORY: string[] }>({
    SYSTEM: [],
    CATEGORY: [],
  });
  const groupingType = React.useMemo<GroupingType>(
    () => (event.type === "DIAGNOSED" ? (displayAllQuantities ? "SYSTEM" : "CATEGORY") : "SYSTEM"),
    [displayAllQuantities, event.type],
  );

  const customGroupOrder = React.useMemo(
    () => [
      getIndicatorsLabel("performance"),
      getRoleLabel("observable"),
      PROMOTION_FILTERS,
      OTHERS,
    ],
    [],
  );

  const groups = React.useMemo(() => {
    if (!groupingType) return [];
    const getQuantityGroups = getQuantityGroupsByGroupingType[groupingType];
    const quantityGroupIndex = groupBy(searchedQuantities, getQuantityGroups);
    const getGroupTitle = getQuantityGroupTitleByGroupingType[groupingType];

    return Array.from(quantityGroupIndex.entries())
      .map(([groupValue, groupQuantities]) => ({
        id: groupValue,
        title:
          groupingType === "SYSTEM"
            ? (groupQuantities[0]?.systemId ?? t("measuresSymptoms.collapse.noGroup"))
            : getGroupTitle(groupValue),
        quantities: groupQuantities,
      }))
      .sort((a, b) => {
        const idxA = customGroupOrder.indexOf(a.id);
        const idxB = customGroupOrder.indexOf(b.id);
        if (idxA === -1 && idxB === -1) return a.title.localeCompare(b.title);
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
      });
  }, [groupingType, searchedQuantities, customGroupOrder]);

  const groupQuantityBySystemOrRoles = React.useMemo(
    () =>
      groups
        .filter(
          group => !(group.id === PROMOTION_FILTERS && page !== QuantitySelectorType.Measures),
        )
        .map(group => {
          const isGroupOpen = openGroups[groupingType].includes(group.id);
          const groupQuantities = group.quantities;

          const observables = setup ? setup.diagnosisInputs : [];
          const observableQuantities = groupQuantities.filter(q =>
            observables.some(o => o.quantityId === q.id),
          );
          const hasCurrentAlert = groupQuantities.some(
            quantity => quantityAlerts[quantity.id]?.isInAlert,
          );
          const alertInRange =
            page === QuantitySelectorType.Symptoms
              ? observableQuantities[0]
                ? quantityAlerts[observableQuantities[0].id]!.inRange.map((_, i) =>
                    observableQuantities.map(quantity => quantityAlerts[quantity.id]!.inRange[i]),
                  )
                : []
              : (quantityAlerts[groupQuantities[0]!.id]!.inRange.map((_, i) =>
                  groupQuantities.map(quantity => quantityAlerts[quantity.id]?.inRange[i]),
                ) ?? []);
          const alertCount = alertInRange.filter(i => i.some(j => j)).length;
          const analysisCount = alertInRange.filter(i => i.some(j => j !== null)).length;
          const alertPercentage = (alertCount / analysisCount) * 100;

          const title = (
            <div className={sectionTitle}>
              <Ellipsis title={group.title}>{group.title}</Ellipsis>
              {allAnalysesMode ? null : (
                <AlertBadge
                  withIcon={hasCurrentAlert}
                  alertPercentage={alertCount && alertPercentage}
                  alertType={alertType}
                />
              )}
            </div>
          );

          return (
            <GroupSection
              key={group.id}
              disabled={selectedQuantities.some(cq => {
                const selectedEnchancedQuantity = enhancedQuantities.find(
                  eq => eq.id === cq.quantity.id,
                );
                if (!selectedEnchancedQuantity) return false;
                return getQuantityGroupsByGroupingType[groupingType](
                  selectedEnchancedQuantity,
                ).includes(group.id);
              })}
              title={
                alertCount !== 0 || hasCurrentAlert ? (
                  <Tooltip
                    overlay={
                      <QuantityGroupCard
                        name={group.title}
                        alertMessage={
                          <AlertMessage
                            alertType={alertType}
                            currentMessage={
                              hasCurrentAlert
                                ? alertType === "measures"
                                  ? t("quantitySelector.group.alert.measures.current")
                                  : t("quantitySelector.group.alert.symptoms.current")
                                : null
                            }
                            message={
                              alertCount
                                ? alertType === "measures"
                                  ? t("quantitySelector.group.alert.measures.inRange", {
                                      alertCount,
                                      analysisCount,
                                      alertPercentage: formatNumber(alertPercentage, {
                                        precision: 0,
                                      }),
                                    })
                                  : t("quantitySelector.group.alert.symptoms.inRange", {
                                      alertCount,
                                      analysisCount,
                                      alertPercentage: formatNumber(alertPercentage, {
                                        precision: 0,
                                      }),
                                    })
                                : null
                            }
                          />
                        }
                      />
                    }
                  >
                    {title}
                  </Tooltip>
                ) : (
                  title
                )
              }
              onToggle={() =>
                setOpenGroups({
                  ...openGroups,
                  [groupingType]: isGroupOpen
                    ? openGroups[groupingType].filter(g => g !== group.id)
                    : [...openGroups[groupingType], group.id],
                })
              }
              opened={
                selectedQuantities.some(cq => {
                  const selectedEnchancedQuantity = enhancedQuantities.find(
                    eq => eq.id === cq.quantity.id,
                  );
                  if (!selectedEnchancedQuantity) return false;
                  return getQuantityGroupsByGroupingType[groupingType](
                    selectedEnchancedQuantity,
                  ).includes(group.id);
                }) || isGroupOpen
              }
            >
              <SelectableQuantitiesGroup
                projectId={project.id}
                quantities={groupQuantities}
                selectedQuantities={selectedQuantities}
                setSelectedQuantities={setSelectedQuantities}
                showQuantityDescriptions={showQuantityDescriptions}
                onlyFirstDisplayed={onlyFirstDisplayed}
                quantityAlerts={quantityAlerts}
                activationFiltersByQuantityId={activationFiltersByQuantityId}
                crossFiltersByQuantityName={crossFiltersByQuantityName}
                allAnalysesMode={allAnalysesMode}
                alertType={alertType}
                handleEditQuantity={handleEditQuantity}
                handleDeleteQuantity={handleDeleteQuantity}
                isDrawerOpen={isDrawerOpen}
              />
            </GroupSection>
          );
        }),
    [
      groups,
      page,
      openGroups,
      groupingType,
      setup,
      quantityAlerts,
      allAnalysesMode,
      selectedQuantities,
      alertType,
      project.id,
      setSelectedQuantities,
      showQuantityDescriptions,
      onlyFirstDisplayed,
      activationFiltersByQuantityId,
      crossFiltersByQuantityName,
      handleEditQuantity,
      handleDeleteQuantity,
      isDrawerOpen,
      enhancedQuantities,
    ],
  );

  const hasActiveQuantityFilters = Object.values(filteredValues).some(arr => arr.length > 0);

  const [tab, setTab] = useState<"general-information" | "formulae">("general-information");

  return (
    <>
      <div className={wrapper}>
        <div className={filtersContainer}>
          <div className={filtersFirstLine}>
            <SearchInputV2
              data-cy="search"
              placeholder={t("project.quantities.search.name_or_description")}
              value={search}
              onChange={handleSearchChange}
              className={searchInput}
            />
            <div className={filterButtonWrapper}>
              <TooltipV2 overlay={t("quantitySelector.filter.tooltip")}>
                <ButtonIconV2
                  onClick={toggleShowQuantityFilters}
                  className={cx(activeButtonIcon, { active: showQuantityFilters })}
                >
                  {hasActiveQuantityFilters && <ButtonIconMarker />}
                  <Filter3Fill height={24} width={24} />
                </ButtonIconV2>
              </TooltipV2>
              <ChartMenu
                marker={
                  showOnlyAlertQuantities ||
                  showQuantityDescriptions ||
                  (canUserViewIntermediateCalculations && showIntermediateCalculations) ||
                  (event.type === "DIAGNOSED" && displayAllQuantities)
                }
                className={quantitySelectorChartMenu}
                tooltipOverlay={t("quantitySelector.display_options.tooltip")}
              >
                {event.type === "DIAGNOSED" && (
                  <RelevantQuantitiesSwitch
                    dataType={page}
                    checked={displayAllQuantities}
                    onChange={() => setDisplayAllQuantities(s => !s)}
                  />
                )}
                {canUserViewIntermediateCalculations && (
                  <Switch
                    checked={showIntermediateCalculations}
                    onChange={() => setShowIntermediateCalculations(value => !value)}
                    position="left"
                  >
                    {t("quantitySelector.toggle.showIntermediateCalculations")}
                  </Switch>
                )}
                <Switch
                  checked={showOnlyAlertQuantities}
                  onChange={() => setShowOnlyAlertQuantities(!showOnlyAlertQuantities)}
                  position="left"
                >
                  {page === QuantitySelectorType.Symptoms
                    ? t("quantitySelector.toggle.symptomatic")
                    : t("quantitySelector.toggle.unavailable")}
                </Switch>
                <Switch
                  checked={showQuantityDescriptions}
                  onChange={() => setShowQuantityDescriptions(!showQuantityDescriptions)}
                  position="left"
                >
                  {t("quantitySelector.toggle.showDescriptions")}
                </Switch>
              </ChartMenu>
            </div>
          </div>

          {showQuantityFilters && (
            <div className={filterWrapper}>
              <FilterSelects
                wrappable
                data={sortedQuantities}
                metadata={getQuantitiesFilterMetadata(showRolesFilter, hideUnitsFilter)}
                filterValues={filteredValues}
                setFilterValues={setFilteredValues}
                defaultFilterValues={DEFAULT_QUANTITY_FILTER_VALUES}
              />
            </div>
          )}
        </div>
        <BodyWrapper>
          {searchedQuantities.length ? (
            <div className={body} data-cy="body-quantities">
              {groupQuantityBySystemOrRoles}
            </div>
          ) : (
            <div className={blankstateOverflow}>
              <BlankStateV2
                title={t("measuresSymptoms.blankstate.no_quantities_found")}
                picto={<NoData size="m" />}
                data-cy="blankstate"
              />
            </div>
          )}
        </BodyWrapper>
        <div className={footerContainer}>
          {canUserEditQuantities && (
            <Button
              leftIcon={<AddCircleLine size="s" />}
              variant="secondary"
              slim
              className={footerActionButton}
              onClick={() => {
                setQuantityName(null);
                setIsCreateDrawerOpen(true);
              }}
              disabled={isDrawerOpen}
              title={
                isDrawerOpen ? t("quantitySelector.actionDisabledBecauseDrawerOpen") : undefined
              }
            >
              {t("quantitySelector.actions.create")}
            </Button>
          )}
          <Button
            leftIcon={<ShareBoxLine color={color.titanium800} size="s" />}
            variant="tertiary"
            className={footerActionButton}
            slim
            onClick={() =>
              window.open(
                toBasenamePath(
                  toPath("/projects/:projectId/settings/quantities", { projectId: project.id }),
                ),
              )
            }
          >
            {t("quantitySelector.actions.viewList")}
          </Button>
        </div>
      </div>
      <Drawer
        title={t("project.settings.quantity.side_panel.create.title")}
        isOpen={isCreateDrawerOpen}
        className={noPaddingDrawer}
        onRequestClose={handleCloseDrawer}
        header={
          <div className={headerContainer}>
            <div className={titleBlock}>
              <Text type="h4">{t("project.settings.quantity.side_panel.create.title")}</Text>
              {quantityName ? (
                <Text type="body1" className={titleSubtitle}>
                  {quantityName}
                </Text>
              ) : null}
            </div>
            <ButtonIconV2 onClick={handleCloseDrawer}>
              <CloseLine size="s" color={colors.carbon800} />
            </ButtonIconV2>
            <NavV2>
              <Tab
                id="general-information"
                className={cx({ active: tab === "general-information" })}
                onClick={() => setTab("general-information")}
              >
                {t("project.settings.quantity.side_panel.tab.general_information")}
              </Tab>
              <Tab
                id="formula"
                className={cx({ active: tab === "formulae" })}
                onClick={() => setTab("formulae")}
              >
                {t("project.settings.quantity.side_panel.tab.formulae")}
              </Tab>
            </NavV2>
          </div>
        }
      >
        <CreateFormulaQuantity
          view={tab}
          ref={createQuantityRef}
          projectId={project.id}
          closePanel={handleCloseDrawer}
          className={quantityPanelWrapper}
          timezone={project.timezone}
          onNameChange={setQuantityName}
        />
      </Drawer>
      <Drawer
        title={t("project.settings.quantity.side_panel.edit.title")}
        isOpen={isEditDrawerOpen}
        className={noPaddingDrawer}
        onRequestClose={handleCloseEditDrawer}
        header={
          <div className={headerContainer}>
            <div className={titleBlock}>
              <Text type="h4">{t("project.settings.quantity.side_panel.edit.title")}</Text>
              {quantityToModify ? (
                <Text type="body1" className={titleSubtitle}>
                  {quantityName ?? quantityToModify.name}
                </Text>
              ) : null}
            </div>
            <ButtonIconV2 onClick={handleCloseEditDrawer}>
              <CloseLine size="s" color={colors.carbon800} />
            </ButtonIconV2>
            <NavV2>
              <Tab
                id="general-information"
                className={cx({ active: tab === "general-information" })}
                onClick={() => setTab("general-information")}
              >
                {t("project.settings.quantity.side_panel.tab.general_information")}
              </Tab>
              {quantityToModify?.type === "FORMULA" && (
                <Tab
                  id="formula"
                  className={cx({ active: tab === "formulae" })}
                  onClick={() => setTab("formulae")}
                >
                  {t("project.settings.quantity.side_panel.tab.formulae")}
                </Tab>
              )}
            </NavV2>
          </div>
        }
      >
        {isEditDrawerOpen && quantityToModify && (
          <EditQuantity
            view={tab}
            ref={editQuantityRef}
            projectId={project.id}
            closePanel={handleCloseEditDrawer}
            className={quantityPanelWrapper}
            quantity={quantityToModify}
            timezone={project.timezone}
            onNameChange={setQuantityName}
          />
        )}
      </Drawer>
      {isDeleteModalOpen && quantityToModify && (
        <Modal title={t("project.quantities.delete.title")} isOpen onRequestClose={closeModal}>
          <ConfirmDelete
            projectId={project.id}
            onRequestClose={closeModal}
            quantity={quantityToModify}
            handleSubmit={handleDelete}
          />
        </Modal>
      )}
    </>
  );
};

export default React.memo(QuantitySelector);

//#region CSS
const BodyWrapper = styled(StyledScrollbarDiv())`
  position: relative;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 16px;
  padding-right: 16px;
  border-top: 1px solid;
  border-top-color: ${color.titanium200};
  transform: translate3d(0, 0, 0);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.m};

  align-self: stretch;
`;

const GroupSection = styled(Collapse)`
  content-visibility: auto;
  & + & {
    margin-top: 20px;
  }
`;

const quantityPanelWrapper = css`
  & .${submit} {
    background-color: ${colors.hydrogen};
  }
`;

const body = css`
  height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-right: 16px;
`;

const sectionTitle = css`
  display: flex;
  gap: 4px;
  color: ${color.carbon900};
  font: ${fonts.body1SemiBold};
  width: 100%;
`;

const wrapper = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.m};
  flex: 1 0 0;
  align-self: stretch;
  height: 100%;
`;

const blankstateOverflow = css`
  height: 0;
  & > div {
    height: auto;
    padding: 0 20px;
    justify-content: flex-start;
  }
`;

const activeButtonIcon = css`
  &.active {
    color: ${color.lightNitrogen500_Main};
    background-color: ${color.lightNitrogen50};
  }
`;

const filtersContainer = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${spacings.m};
  align-self: stretch;
  width: 100%;
`;

const filtersFirstLine = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  align-self: stretch;
`;

const filterButtonWrapper = css`
  display: flex;
  gap: ${spacings.xs};
  align-items: center;
`;

const quantitySelectorChartMenu = css`
  & button {
    height: 36px;
    width: 36px;
  }
`;

const searchInput = css`
  flex-grow: 1;
  min-width: 0;
  margin: 0;
`;

const filterWrapper = css`
  display: flex;
  align-items: flex-start;
  column-gap: ${spacings.xxs};
`;

const footerContainer = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacings.m};
  align-self: stretch;
  position: sticky;
  background: ${colors.titanium50};
  bottom: 0;
  border-top: 1px solid ${color.titanium200};
  padding-top: ${spacings.m};
  padding-bottom: ${spacings.m};
  z-index: 1;
`;

const footerActionButton = css`
  padding: ${spacings.xs};
  flex: 1 0 0;
  gap: ${spacings.xxs};
`;

const noPaddingDrawer = css`
  .drawer-wrapper {
    padding: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

const headerContainer = css`
  z-index: 2;
  position: sticky;
  top: 0;

  display: grid;
  grid-template-columns: 1fr max-content;
  grid-template-rows: repeat(2, max-content);

  background-color: ${colors.hydrogen};

  & > *:nth-child(1),
  & > *:nth-child(2) {
    align-self: center;
    margin: 16px 16px;
  }

  nav {
    border-bottom: 1px solid ${colors.titanium100};
    grid-column: 1 / -1;
    grid-row: 2/3;
  }
`;

const titleBlock = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const titleSubtitle = css`
  color: ${color.carbon700};
  min-width: 0;
  overflow-wrap: anywhere;
`;
//#endregion CSS
